import * as Yup from 'yup';

/**
 * Yup validation schema for getting license reporting data.
 *
 * @constant
 * @type {object}
 * @property {Yup.StringSchema} timePeriod - Validation for the time period.
 * @property {Yup.DateSchema} customStartDate - Validation for the custom start date.
 * @property {Yup.DateSchema} customEndDate - Validation for the custom end date.
 */
const getLicenseReportingDataValidation = Yup.object({
    // Allow null; we'll enforce rules in tests below
    timePeriod: Yup.string().trim().nullable(),
    customStartDate: Yup.date().nullable(),
    customEndDate: Yup.date().nullable()
})
    // 1) Require either a preset timePeriod or a full custom range
    .test('time-or-custom', function (value) {
        const { timePeriod, customStartDate, customEndDate } = value || {};

        // If a preset period (other than 'custom') is chosen -> OK
        if (timePeriod && timePeriod !== 'custom') return true;

        // If 'custom' is chosen, both dates are required
        if (timePeriod === 'custom') {
            if (!customStartDate) {
                return this.createError({
                    path: 'customStartDate',
                    message: 'Start date required'
                });
            }
            if (!customEndDate) {
                return this.createError({
                    path: 'customEndDate',
                    message: 'End date required'
                });
            }
            return true;
        }

        // If no timePeriod provided, but user supplied one/both dates -> require both
        if (customStartDate || customEndDate) {
            if (!customStartDate) {
                return this.createError({
                    path: 'customStartDate',
                    message: 'Start date required'
                });
            }
            if (!customEndDate) {
                return this.createError({
                    path: 'customEndDate',
                    message: 'End date required'
                });
            }
            return true;
        }

        // Nothing selected -> require a preset timePeriod
        return this.createError({
            path: 'timePeriod',
            message: 'Required'
        });
    })
    // 2) Range check when both dates exist
    .test(
        'end-after-start',
        'End date cannot be before start date',
        (value) => {
            const { customStartDate, customEndDate } = value || {};
            if (!customStartDate || !customEndDate) return true;
            return new Date(customEndDate) >= new Date(customStartDate);
        }
    );

export default getLicenseReportingDataValidation;
